/**
 * MessageList (Sprint 7 B5).
 *
 * Scrollable list message + auto-scroll to bottom khi message mới hoặc partial update.
 * Suggested questions card khi chưa có message (empty state).
 */

import { useEffect, useRef } from "react";
import { Button, Card, Empty, Space, Typography } from "antd";
import { BulbOutlined } from "@ant-design/icons";
import type { Citation, Message as ChatMessage } from "@/api/learning";
import { MessageBubble } from "./MessageBubble";

const { Text, Title } = Typography;

interface Props {
  messages: ChatMessage[];
  partial: string;
  partialCitations: Citation[];
  streaming: boolean;
  suggestedQuestions?: string[];
  onSuggestionClick?: (text: string) => void;
}

const DEFAULT_SUGGESTIONS = [
  "Tóm tắt nội dung bài này",
  "Có ví dụ thực tế nào không?",
  "Phần nào trong bài là quan trọng nhất?",
];

export function MessageList({
  messages,
  partial,
  partialCitations,
  streaming,
  suggestedQuestions = DEFAULT_SUGGESTIONS,
  onSuggestionClick,
}: Props) {
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll khi list update. jsdom (test env) không có scrollIntoView nên
  // check function tồn tại trước khi gọi.
  useEffect(() => {
    const el = bottomRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages.length, partial, streaming]);

  const isEmpty = messages.length === 0 && !streaming && !partial;

  return (
    <div
      data-testid="message-list"
      style={{
        flex: 1,
        overflowY: "auto",
        padding: 16,
        background: "#fafafa",
      }}
    >
      {isEmpty ? (
        <div style={{ padding: 16 }}>
          <Empty
            image={<BulbOutlined style={{ fontSize: 48, color: "#722ed1" }} />}
            imageStyle={{ height: 60 }}
            description={
              <Text type="secondary">
                Hỏi AI Tutor bất cứ điều gì về bài học này.
              </Text>
            }
          >
            <Card size="small" title="Câu hỏi gợi ý" style={{ marginTop: 16, textAlign: "left" }}>
              <Space direction="vertical" style={{ width: "100%" }}>
                {suggestedQuestions.map((q) => (
                  <Button
                    key={q}
                    type="dashed"
                    block
                    onClick={() => onSuggestionClick?.(q)}
                    data-testid="suggested-question"
                    style={{ textAlign: "left", whiteSpace: "normal", height: "auto", padding: "8px 12px" }}
                  >
                    {q}
                  </Button>
                ))}
              </Space>
            </Card>
          </Empty>
        </div>
      ) : (
        <>
          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              role={m.role}
              content={m.content}
              citations={m.citations}
            />
          ))}
          {/* Streaming partial assistant message */}
          {(streaming || partial) && (
            <MessageBubble
              role="assistant"
              content={partial}
              citations={partialCitations}
              streaming={streaming}
            />
          )}
        </>
      )}
      <div ref={bottomRef} />
    </div>
  );
}

// Re-export Title để parent dùng (Drawer header)
export { Title };
